/**
 * Routes and route guards.
 *
 * Three top-level areas, one per persona the product owner asked for: `/patient`,
 * `/clinician`, `/org`. The coordinator lives inside `/org` as a permission set rather
 * than a fourth area — see DECISIONS.md D-02, which also records how to promote it to a
 * top-level area if that call turns out to be wrong.
 *
 * Guards here are UX, not security. `RequireAuth` and `RequirePermission` stop a user
 * from navigating into a screen that would render empty or error; Firestore rules are
 * what actually stop a request. Anyone auditing access control should read
 * `firestore.rules`.
 */
import { Suspense, lazy, type ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ROLE_LABELS, type Permission } from '@rescure/shared'
import { useAuth } from './lib/auth'
import { AppShell } from './components/layout/AppShell'
import { RouteErrorBoundary } from './components/layout/RouteErrorBoundary'
import { Alert, EmptyState, Spinner } from './components/ui/primitives'

// Code-split per area. A patient never downloads the sponsor feasibility bundle with its
// charting library, which matters because the patient view is the one most likely to be
// opened on a phone on a clinic's wifi.
const SignIn = lazy(() => import('./routes/SignIn'))
const SignUp = lazy(() => import('./routes/SignUp'))
const Landing = lazy(() => import('./routes/Landing'))

const PatientMatches = lazy(() => import('./routes/patient/PatientMatches'))
const PatientSearch = lazy(() => import('./routes/patient/PatientSearch'))
const Onboarding = lazy(() => import('./routes/patient/Onboarding'))
const PatientRecord = lazy(() => import('./routes/patient/PatientRecord'))
const PatientPrivacy = lazy(() => import('./routes/patient/PatientPrivacy'))
const PatientTrialDetail = lazy(() => import('./routes/patient/PatientTrialDetail'))

const ClinicianPanel = lazy(() => import('./routes/clinician/ClinicianPanel'))
const ClinicianPatientDetail = lazy(() => import('./routes/clinician/ClinicianPatientDetail'))
const ClinicianTrials = lazy(() => import('./routes/clinician/ClinicianTrials'))
const ClinicianReferrals = lazy(() => import('./routes/clinician/ClinicianReferrals'))
const MatchDetail = lazy(() => import('./routes/clinician/MatchDetail'))

const OrgOverview = lazy(() => import('./routes/org/OrgOverview'))
const OrgPipeline = lazy(() => import('./routes/org/OrgPipeline'))
const OrgPatientDetail = lazy(() => import('./routes/org/OrgPatientDetail'))
const OrgFeasibility = lazy(() => import('./routes/org/OrgFeasibility'))
const OrgTrials = lazy(() => import('./routes/org/OrgTrials'))
const OrgTrialDetail = lazy(() => import('./routes/org/OrgTrialDetail'))
const OrgSettings = lazy(() => import('./routes/org/OrgSettings'))

function FullPageSpinner({ label }: { label: string }) {
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner label={label} />
    </div>
  )
}

/**
 * Gate on being signed in.
 *
 * Waits for `loading` to resolve before deciding. Redirecting while auth is still
 * resolving is the bug that makes an authenticated user see a flash of the sign-in
 * screen on every reload, which on a clinical tool reads as "it logged me out again".
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, error } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageSpinner label="Checking your session" />

  if (!user) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />
  }

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Alert tone="error" title="We could not load your account">
          {error}
        </Alert>
      </div>
    )
  }

  return <>{children}</>
}

/**
 * Gate on a specific permission.
 *
 * Renders an explanation rather than redirecting. A silent bounce to the home screen
 * leaves the user with no idea why the link they clicked did nothing; naming the missing
 * permission at least makes it reportable.
 */
function RequirePermission({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can, role } = useAuth()

  if (!can(permission)) {
    return (
      <EmptyState
        title="You do not have access to this screen"
        hint={
          <>
            Your role{role ? ` (${ROLE_LABELS[role] ?? role})` : ''} does not include access to this screen. If you
            believe this is wrong, ask your organization administrator to check your role.
          </>
        }
      />
    )
  }

  return <>{children}</>
}

/**
 * Gate on being signed in as a specific role.
 *
 * `/patient/*` reads `usePatientSelf()` throughout, which only resolves a `patientId` for
 * `PATIENT` accounts — a clinician or org member reaching one of these routes by a stale
 * bookmark or a typed URL would just see an empty "no record linked" state, but there is no
 * reason to let them land there at all when the nav never offers it.
 */
function RequireRole({ role, children }: { role: string; children: ReactNode }) {
  const { role: actualRole, home } = useAuth()
  if (actualRole !== role) return <Navigate to={home} replace />
  return <>{children}</>
}

/** Signed in: whichever area the role belongs to. Signed out: the public front page. */
function HomeRedirect() {
  const { loading, user, home } = useAuth()
  if (loading) return <FullPageSpinner label="Loading Rescure" />
  return user ? <Navigate to={home} replace /> : <Landing />
}

export default function App() {
  const location = useLocation()
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<FullPageSpinner label="Loading" />}>
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/" element={<HomeRedirect />} />

        {/* Retired: the direct-LLM demo route. Kept as a redirect so any bookmarked link
            still lands somewhere sensible instead of a blank screen. */}
        <Route path="/ai-match" element={<Navigate to="/" replace />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          {/* --- patient ------------------------------------------------- */}
          <Route
            path="/patient"
            element={
              <RequireRole role="PATIENT">
                <PatientMatches />
              </RequireRole>
            }
          />
          <Route
            path="/patient/welcome"
            element={
              <RequireRole role="PATIENT">
                <Onboarding />
              </RequireRole>
            }
          />
          <Route
            path="/patient/search"
            element={
              <RequireRole role="PATIENT">
                <PatientSearch />
              </RequireRole>
            }
          />
          <Route
            path="/patient/record"
            element={
              <RequireRole role="PATIENT">
                <PatientRecord />
              </RequireRole>
            }
          />
          <Route
            path="/patient/privacy"
            element={
              <RequireRole role="PATIENT">
                <PatientPrivacy />
              </RequireRole>
            }
          />
          <Route
            path="/patient/trial/:trialId"
            element={
              <RequireRole role="PATIENT">
                <PatientTrialDetail />
              </RequireRole>
            }
          />

          {/* --- clinician ----------------------------------------------- */}
          <Route
            path="/clinician"
            element={
              <RequirePermission permission="patient:read:panel">
                <ClinicianPanel />
              </RequirePermission>
            }
          />
          <Route
            path="/clinician/patient/:patientId"
            element={
              <RequirePermission permission="patient:read:panel">
                <ClinicianPatientDetail />
              </RequirePermission>
            }
          />
          <Route
            path="/clinician/match/:candidateId"
            element={
              <RequirePermission permission="match:read:identified">
                <MatchDetail />
              </RequirePermission>
            }
          />
          <Route path="/clinician/trials" element={<ClinicianTrials />} />
          <Route
            path="/clinician/referrals"
            element={
              <RequirePermission permission="candidate:shortlist">
                <ClinicianReferrals />
              </RequirePermission>
            }
          />

          {/* --- trial organization -------------------------------------- */}
          <Route path="/org" element={<OrgOverview />} />
          <Route path="/org/pipeline" element={<OrgPipeline />} />
          <Route
            path="/org/patient/:patientId"
            element={
              <RequirePermission permission="patient:read:org">
                <OrgPatientDetail />
              </RequirePermission>
            }
          />
          <Route
            path="/org/feasibility"
            element={
              <RequirePermission permission="cohort:query">
                <OrgFeasibility />
              </RequirePermission>
            }
          />
          <Route path="/org/trials" element={<OrgTrials />} />
          <Route path="/org/trials/:trialId" element={<OrgTrialDetail />} />
          <Route
            path="/org/settings"
            element={
              <RequirePermission permission="org:manage">
                <OrgSettings />
              </RequirePermission>
            }
          />

        </Route>

        <Route
          path="*"
          element={
            <div className="grid min-h-dvh place-items-center px-4">
              <EmptyState
                title="Page not found"
                hint="Check the address, or head back to the start."
                action={
                  <Link to="/" className="text-sm font-medium text-brand-700 underline underline-offset-2">
                    Go to Rescure home
                  </Link>
                }
              />
            </div>
          }
        />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  )
}
